import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CanDeactivate, Router } from '@angular/router';
import { map, of } from 'rxjs';

import { openConfirmDialog } from '../components/generics/confirm-dialog/confirm-dialog.helper';

export interface UnsavedChangesGuardedComponent {
  hasUnsavedChanges: boolean;
  dialog?: MatDialog;
  unsavedChangesMessage?: string;
  backRedirectRoute?: string;
  onLeaveConfirm?: () => void;
}

// Prevents Leaving the page if there are unsaved changes, only works for in-app and browser back/forward navigation, DOES NOT work for reload
@Injectable({ providedIn: 'root' })
export class UnsavedChangesGuard implements CanDeactivate<UnsavedChangesGuardedComponent> {
  dialog = inject(MatDialog);
  router = inject(Router);
  canDeactivate(component: UnsavedChangesGuardedComponent) {
    if (!component) {
      return of(true);
    }

    const bypassGuard = this.router.currentNavigation()?.extras?.state?.['bypassGuard'];
    if (!component.hasUnsavedChanges || bypassGuard) {
      if (bypassGuard) {
        this.router.navigate([this.router.url], {
          replaceUrl: true,
          state: { bypassGuard: false }
        });
      }
      component.dialog?.closeAll();
      return of(true);
    }

    const dialogRef = openConfirmDialog(this.dialog, {
      title: 'Leave?',
      message: component.unsavedChangesMessage || 'If you leave now, your progress will be lost.',
      leftButton: 'Leave',
      rightButton: 'Stay on page',
      discourageConfirmation: true
    });

    return dialogRef.afterClosed().pipe(
      map(confirmed => {
        if (confirmed) {
          component.dialog?.closeAll();
          if (component.onLeaveConfirm) component.onLeaveConfirm();
          if (component.backRedirectRoute)
            this.router.navigate([component.backRedirectRoute], { state: { bypassGuard: true } });
          return true; // Allow navigation if user really confirmed to leave
        }
        return false;
      })
    );
  }
}
